import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  listUserDecks,
  saveDeck,
  deleteUserDeck,
  cloneDeck,
  getGameIcon,
  getCardgames,
  getGameFormats,
  saveActionLog
} from '../../services/supabaseService';
import { getGameNameById } from '../../constants/deckRules';
import { exportDeckToText } from '../../utils/deckExporter';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { CardImage } from '../../components/CardImage';

interface MyDecksPageProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
  dbGames?: { id: any; name: string; slug?: string }[];
}

interface FormatOption {
  id: number | string;
  name: string;
  slug?: string;
  description?: string | null;
}

export const MyDecksPage: React.FC<MyDecksPageProps> = ({
  activeGame,
  setActiveGame,
  dbGames = []
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [decks, setDecks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGameFilter, setSelectedGameFilter] = useState<string | number>(
    activeGame !== 'All' ? activeGame : 'all'
  );
  const [selectedFormatFilter, setSelectedFormatFilter] = useState<string>('all');
  const [selectedVisibilityFilter, setSelectedVisibilityFilter] = useState<'all' | 'public' | 'private'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'cards' | 'value'>('recent');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Deleting state
  const [deckToDelete, setDeckToDelete] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cloning state
  const [cloningDeckId, setCloningDeckId] = useState<string | null>(null);

  // Edit Info Modal state
  const [deckToEdit, setDeckToEdit] = useState<any | null>(null);
  const [editDeckName, setEditDeckName] = useState('');
  const [editDeckFormatId, setEditDeckFormatId] = useState<string>('');
  const [editDeckIsPublic, setEditDeckIsPublic] = useState(false);
  const [editFormats, setEditFormats] = useState<FormatOption[]>([]);
  const [loadingEditFormats, setLoadingEditFormats] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Options Dropdown Menu state
  const [openMenuDeckId, setOpenMenuDeckId] = useState<string | null>(null);

  useEffect(() => {
    const handleCloseMenu = () => setOpenMenuDeckId(null);
    if (openMenuDeckId) {
      document.addEventListener('click', handleCloseMenu);
      window.addEventListener('scroll', handleCloseMenu, { passive: true });
      return () => {
        document.removeEventListener('click', handleCloseMenu);
        window.removeEventListener('scroll', handleCloseMenu);
      };
    }
  }, [openMenuDeckId]);

  // Games list
  const [gamesList, setGamesList] = useState<{ id: any; name: string; slug?: string }[]>(dbGames);

  // Create Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newDeckName, setNewDeckName] = useState('');
  const [newDeckGameId, setNewDeckGameId] = useState<number | string>(
    activeGame !== 'All' ? activeGame : ''
  );
  const [newDeckFormatId, setNewDeckFormatId] = useState<string>('');
  const [newDeckIsPublic, setNewDeckIsPublic] = useState(false);
  const [availableFormats, setAvailableFormats] = useState<FormatOption[]>([]);
  const [loadingFormats, setLoadingFormats] = useState(false);
  const [isCreatingDeck, setIsCreatingDeck] = useState(false);

  // Open create modal if ?novo=1 in URL
  useEffect(() => {
    if (searchParams.get('novo') === '1') {
      setIsCreateModalOpen(true);
      // clean param without refresh
      searchParams.delete('novo');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Sync games list
  useEffect(() => {
    if (gamesList.length === 0) {
      getCardgames().then((g) => {
        if (Array.isArray(g)) {
          setGamesList(g);
          if (!newDeckGameId && g.length > 0) {
            setNewDeckGameId(g[0].id);
          }
        }
      });
    } else if (!newDeckGameId && gamesList.length > 0) {
      setNewDeckGameId(gamesList[0].id);
    }
  }, [gamesList, newDeckGameId]);

  // Load user decks
  const loadDecks = async () => {
    if (!user) {
      setDecks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Fetch all decks for user across all games
      const data = await listUserDecks();
      setDecks(data || []);
    } catch (err) {
      console.error('Error loading decks:', err);
      showNotification('Erro ao carregar seus decks.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDecks();
  }, [user]);

  // Load formats when game in create modal changes
  useEffect(() => {
    if (!newDeckGameId) {
      setAvailableFormats([]);
      setNewDeckFormatId('');
      return;
    }
    setLoadingFormats(true);
    getGameFormats(newDeckGameId)
      .then((formats: any[]) => {
        const formatted = (formats || []).map((f) => ({
          id: f.id,
          name: f.name,
          slug: f.slug || String(f.name).toLowerCase(),
          description: f.description
        }));
        setAvailableFormats(formatted);
        if (formatted.length > 0) {
          setNewDeckFormatId(String(formatted[0].id));
        } else {
          setNewDeckFormatId('');
        }
      })
      .catch((err) => {
        console.error('Error fetching formats for game:', err);
        setAvailableFormats([]);
      })
      .finally(() => {
        setLoadingFormats(false);
      });
  }, [newDeckGameId]);

  // Helper to extract formats present in user's loaded decks
  const userDecksFormats = useMemo(() => {
    const fmts = new Set<string>();
    decks.forEach((d) => {
      const fmt = d.game_formats?.name || d.data?.format_name || d.data?.format;
      if (fmt && typeof fmt === 'string' && fmt.trim()) {
        fmts.add(fmt.trim());
      }
    });
    return Array.from(fmts);
  }, [decks]);

  // Deck meta helper (leader, total cards, estimated value)
  const processDeckMeta = (deck: any) => {
    const items = deck.data?.items || [];
    const totalCards =
      deck.data?.total ||
      items.reduce((acc: number, curr: any) => acc + (Number(curr.quantity) || 1), 0);

    let leaderCard: any = null;
    let leaderImage: string | null = null;

    // Look for explicit leader/commander or first card
    const explicitLeader = items.find((i: any) => {
      const sec = String(i.section || '').toLowerCase();
      const type = String(i.card?.type || i.card?.cardType || '').toLowerCase();
      return sec === 'leader' || sec === 'commander' || type.includes('leader') || type.includes('commander');
    });

    if (explicitLeader && explicitLeader.card) {
      leaderCard = explicitLeader.card;
      leaderImage =
        leaderCard.imageUrl ||
        leaderCard.image_url ||
        leaderCard.image ||
        leaderCard.images?.small ||
        leaderCard.images?.large ||
        null;
    } else if (items.length > 0 && items[0]?.card) {
      leaderCard = items[0].card;
      leaderImage =
        leaderCard.imageUrl ||
        leaderCard.image_url ||
        leaderCard.image ||
        leaderCard.images?.small ||
        leaderCard.images?.large ||
        null;
    }

    const format = deck.game_formats?.name || deck.data?.format_name || deck.data?.format || 'Padrão';
    const gameName = deck.games?.name || getGameNameById(deck.game_id);

    const estimatedValue = items.reduce((acc: number, curr: any) => {
      const price = Number(curr.card?.price || curr.card?.market_price || curr.card?.current_price || 0);
      return acc + (isNaN(price) ? 0 : price * (Number(curr.quantity) || 1));
    }, 0);

    return {
      totalCards,
      leaderCard,
      leaderImage,
      format,
      gameName,
      estimatedValue
    };
  };

  // Filtered and sorted decks
  const filteredDecks = useMemo(() => {
    return decks
      .filter((deck) => {
        // Search query
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const nameMatch = deck.name?.toLowerCase().includes(q);
          const formatMatch = (deck.data?.format || deck.game_formats?.name || '').toLowerCase().includes(q);
          const cardMatch = (deck.data?.items || []).some((item: any) =>
            item.card?.name?.toLowerCase().includes(q)
          );
          if (!nameMatch && !formatMatch && !cardMatch) {
            return false;
          }
        }

        // Game filter
        if (selectedGameFilter !== 'all') {
          if (String(deck.game_id) !== String(selectedGameFilter)) {
            return false;
          }
        }

        // Format filter
        if (selectedFormatFilter !== 'all') {
          const deckFormat = (deck.game_formats?.name || deck.data?.format_name || deck.data?.format || '').toLowerCase();
          if (deckFormat !== selectedFormatFilter.toLowerCase()) {
            return false;
          }
        }

        // Visibility filter
        if (selectedVisibilityFilter === 'public' && !deck.is_public) return false;
        if (selectedVisibilityFilter === 'private' && deck.is_public) return false;

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'name') {
          return (a.name || '').localeCompare(b.name || '');
        }
        if (sortBy === 'cards') {
          const totalA = a.data?.total || (a.data?.items || []).reduce((acc: number, curr: any) => acc + (curr.quantity || 1), 0);
          const totalB = b.data?.total || (b.data?.items || []).reduce((acc: number, curr: any) => acc + (curr.quantity || 1), 0);
          return totalB - totalA;
        }
        if (sortBy === 'value') {
          const metaA = processDeckMeta(a);
          const metaB = processDeckMeta(b);
          return metaB.estimatedValue - metaA.estimatedValue;
        }
        // default: recent
        const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
        const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
        return dateB - dateA;
      });
  }, [decks, searchQuery, selectedGameFilter, selectedFormatFilter, selectedVisibilityFilter, sortBy]);

  // Overall Statistics
  const stats = useMemo(() => {
    const totalDecks = decks.length;
    const publicDecks = decks.filter((d) => d.is_public).length;
    const privateDecks = totalDecks - publicDecks;
    const totalCards = decks.reduce((acc, d) => {
      const cnt = d.data?.total || (d.data?.items || []).reduce((sub: number, c: any) => sub + (Number(c.quantity) || 1), 0);
      return acc + cnt;
    }, 0);
    const uniqueGames = new Set(decks.map((d) => d.game_id).filter(Boolean)).size;

    return { totalDecks, publicDecks, privateDecks, totalCards, uniqueGames };
  }, [decks]);

  // Create deck handler -> saves initial deck and navigates to deckbuilder
  const handleCreateDeckSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeckName.trim()) {
      showNotification('Digite um nome para o baralho.', 'error');
      return;
    }
    if (!newDeckGameId) {
      showNotification('Selecione o jogo para o baralho.', 'error');
      return;
    }

    setIsCreatingDeck(true);
    try {
      const selectedFormat = availableFormats.find((f) => String(f.id) === String(newDeckFormatId));
      const formatIdNumber = newDeckFormatId && !isNaN(Number(newDeckFormatId)) ? Number(newDeckFormatId) : null;

      const initialData = {
        items: [],
        format: selectedFormat?.slug || 'classic',
        format_id: formatIdNumber,
        format_name: selectedFormat?.name || 'Padrão',
        format_slug: selectedFormat?.slug || 'classic',
        total: 0
      };

      const newId = await saveDeck(
        newDeckName.trim(),
        String(newDeckGameId),
        initialData,
        newDeckIsPublic,
        undefined,
        formatIdNumber
      );

      if (newId) {
        await saveActionLog('create_deck', 'user_decks', String(newId), {
          name: newDeckName.trim(),
          game: newDeckGameId,
          message: `criou o baralho "${newDeckName.trim()}"`
        });

        showNotification('Deck criado com sucesso! Abrindo o editor...', 'success');
        setIsCreateModalOpen(false);

        // Update activeGame if setActiveGame is available
        if (setActiveGame && newDeckGameId) {
          setActiveGame(Number(newDeckGameId));
        }

        // Navigate directly to Deckbuilder editor with this deck loaded!
        navigate(`/deckbuilder?deckId=${newId}`);
      }
    } catch (err) {
      console.error('Error creating deck:', err);
      showNotification('Erro ao criar o deck. Tente novamente.', 'error');
    } finally {
      setIsCreatingDeck(false);
    }
  };

  // Clone deck
  const handleCloneDeck = async (deck: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setCloningDeckId(deck.id);
    try {
      const newDeckId = await cloneDeck(deck.id);
      showNotification(`Deck "${deck.name}" clonado com sucesso!`, 'success');
      loadDecks();
    } catch (err: any) {
      console.error('Error cloning deck:', err);
      showNotification('Não foi possível duplicar o deck.', 'error');
    } finally {
      setCloningDeckId(null);
    }
  };

  // Open modal to edit deck metadata (name, format, visibility)
  const openEditInfoModal = (deck: any) => {
    setDeckToEdit(deck);
    setEditDeckName(deck.name || '');
    const currentFmtId = deck.game_format_id || deck.data?.format_id;
    setEditDeckFormatId(currentFmtId ? String(currentFmtId) : '');
    setEditDeckIsPublic(Boolean(deck.is_public));

    setLoadingEditFormats(true);
    getGameFormats(deck.game_id)
      .then((fmts: any[]) => {
        const formatted = (fmts || []).map((f) => ({
          id: f.id,
          name: f.name,
          slug: f.slug || String(f.name).toLowerCase(),
          description: f.description
        }));
        setEditFormats(formatted);
      })
      .catch((err) => {
        console.error('Error fetching formats for edit:', err);
        setEditFormats([]);
      })
      .finally(() => {
        setLoadingEditFormats(false);
      });
  };

  // Save edited deck metadata
  const handleSaveEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!deckToEdit) return;
    if (!editDeckName.trim()) {
      showNotification('Digite um nome para o baralho.', 'error');
      return;
    }

    setIsSavingEdit(true);
    try {
      const selectedFmt = editFormats.find((f) => String(f.id) === String(editDeckFormatId));
      const formatIdNumber = editDeckFormatId && !isNaN(Number(editDeckFormatId)) ? Number(editDeckFormatId) : null;

      const updatedData = {
        ...(deckToEdit.data || {}),
        format: selectedFmt?.slug || deckToEdit.data?.format || 'classic',
        format_id: formatIdNumber,
        format_name: selectedFmt?.name || deckToEdit.data?.format_name || 'Padrão',
        format_slug: selectedFmt?.slug || deckToEdit.data?.format_slug || 'classic'
      };

      await saveDeck(
        editDeckName.trim(),
        String(deckToEdit.game_id),
        updatedData,
        editDeckIsPublic,
        deckToEdit.id,
        formatIdNumber
      );

      setDecks((prev) =>
        prev.map((d) =>
          d.id === deckToEdit.id
            ? {
                ...d,
                name: editDeckName.trim(),
                is_public: editDeckIsPublic,
                game_format_id: formatIdNumber,
                game_formats: selectedFmt ? { id: selectedFmt.id, name: selectedFmt.name } : d.game_formats,
                data: updatedData
              }
            : d
        )
      );

      showNotification('Informações do baralho atualizadas com sucesso!', 'success');
      setDeckToEdit(null);
    } catch (err) {
      console.error('Error updating deck info:', err);
      showNotification('Erro ao salvar informações do baralho.', 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Toggle privacy
  const handleTogglePrivacy = async (deck: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const updatedPrivacy = !deck.is_public;
      await saveDeck(
        deck.name,
        String(deck.game_id),
        deck.data || { items: [] },
        updatedPrivacy,
        deck.id,
        deck.game_format_id
      );

      setDecks((prev) =>
        prev.map((d) => (d.id === deck.id ? { ...d, is_public: updatedPrivacy } : d))
      );
      showNotification(
        updatedPrivacy ? 'Deck definido como Público!' : 'Deck definido como Privado!',
        'success'
      );
    } catch (err) {
      console.error('Error updating privacy:', err);
      showNotification('Erro ao atualizar privacidade do deck.', 'error');
    }
  };

  // Delete deck
  const handleConfirmDelete = async () => {
    if (!deckToDelete) return;
    setIsDeleting(true);
    try {
      await deleteUserDeck(deckToDelete.id);
      await saveActionLog('delete_deck', 'user_decks', deckToDelete.id, {
        name: deckToDelete.name,
        message: `excluiu o baralho "${deckToDelete.name}"`
      });

      setDecks((prev) => prev.filter((d) => d.id !== deckToDelete.id));
      showNotification('Deck excluído com sucesso.', 'success');
      setDeckToDelete(null);
    } catch (err) {
      console.error('Error deleting deck:', err);
      showNotification('Erro ao excluir deck.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Export deck to text
  const handleExportText = (deck: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const gameName = deck.games?.name || getGameNameById(deck.game_id);
      const text = exportDeckToText(gameName, deck.data?.items || []);
      navigator.clipboard.writeText(text);
      showNotification('Lista do baralho copiada em formato texto!', 'success');
    } catch (err) {
      console.error('Error exporting deck:', err);
      showNotification('Erro ao exportar lista do baralho.', 'error');
    }
  };

  // Share link
  const handleShareLink = (deck: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/deck/${deck.id}`;
    navigator.clipboard.writeText(link);
    showNotification('Link público copiado para a área de transferência!', 'success');
  };

  return (
    <div className="min-h-screen pb-20 space-y-5 sm:space-y-6 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-3 sm:pt-4 overflow-x-hidden">
      {/* Header Banner */}
      <div className="relative rounded-3xl overflow-hidden bg-slate-900 border border-slate-800 p-4 sm:p-6 lg:p-8 shadow-md">
        {/* Lightweight radial gradient without expensive Gaussian blur filter */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-[radial-gradient(circle,rgba(147,51,234,0.14)_0%,transparent_70%)] pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 sm:gap-6">
          <div className="space-y-1.5 sm:space-y-2">
            <div className="flex items-center space-x-2 text-xs text-purple-400 font-bold uppercase tracking-wider">
              <i className="fas fa-layer-group"></i>
              <span>Gerenciamento de Baralhos</span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight uppercase italic">
              Meus Decks
            </h1>
            <p className="text-xs sm:text-sm text-slate-400 max-w-2xl font-normal leading-relaxed">
              Crie, organize e gerencie todos os seus baralhos
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full md:w-auto shrink-0">
            <Link
              to="/decks"
              className="px-4 py-2.5 sm:py-3 bg-slate-950/80 hover:bg-slate-800 border border-slate-800 text-slate-300 hover:text-white font-bold uppercase text-xs tracking-wider rounded-xl transition-colors flex items-center justify-center space-x-2 text-center"
              title="Explorar decks públicos da comunidade"
            >
              <i className="fas fa-globe text-slate-400 text-xs"></i>
              <span>Decks da Comunidade</span>
            </Link>

            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-5 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black uppercase text-xs tracking-wider rounded-xl shadow-md shadow-purple-600/20 flex items-center justify-center space-x-2 transition-all active:scale-95 cursor-pointer text-center"
            >
              <i className="fas fa-plus"></i>
              <span>Novo Deck</span>
            </button>
          </div>
        </div>

        {/* Quick Metrics Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-6 border-t border-slate-800/80">
          <div className="bg-slate-950/60 p-3 rounded-xl border border-slate-800/60">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">
              Total de Decks
            </span>
            <span className="text-lg font-black text-white font-mono tabular-nums">
              {stats.totalDecks}
            </span>
          </div>

          <div className="bg-slate-950/60 p-3 rounded-xl border border-slate-800/60">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">
              Total de Cartas
            </span>
            <span className="text-lg font-black text-purple-400 font-mono tabular-nums">
              {stats.totalCards}
            </span>
          </div>

          <div className="bg-slate-950/60 p-3 rounded-xl border border-slate-800/60">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">
              Públicos / Privados
            </span>
            <span className="text-lg font-black text-slate-200 font-mono tabular-nums">
              {stats.publicDecks} <span className="text-slate-500 text-xs font-normal">/</span> {stats.privateDecks}
            </span>
          </div>

          <div className="bg-slate-950/60 p-3 rounded-xl border border-slate-800/60">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">
              Jogos com Baralhos
            </span>
            <span className="text-lg font-black text-emerald-400 font-mono tabular-nums">
              {stats.uniqueGames}
            </span>
          </div>
        </div>
      </div>

      {/* Search and Filters Toolbar */}
      <div className="bg-slate-900/90 border border-slate-800 p-3.5 sm:p-4 rounded-2xl flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 sm:gap-4 shadow-sm">
        {/* Search Input */}
        <div className="relative w-full md:w-72 lg:w-80">
          <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nome ou carta..."
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-9 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white p-1"
            >
              <i className="fas fa-times text-xs"></i>
            </button>
          )}
        </div>

        {/* Filter Selects - Responsive Grid on Mobile, Flex on Desktop (Zero Horizontal Scroll) */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Game Filter */}
          <div className="relative w-full sm:w-auto">
            <select
              value={selectedGameFilter}
              onChange={(e) => setSelectedGameFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              aria-label="Filtrar por Jogo"
              className="w-full sm:w-auto bg-slate-950 border border-slate-800 text-slate-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-purple-500 appearance-none pr-8 cursor-pointer font-bold truncate"
            >
              <option value="all">Todos os Jogos</option>
              {gamesList.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
          </div>
          {/* Visibility Filter */}
          <div className="relative w-full sm:w-auto">
            <select
              value={selectedVisibilityFilter}
              onChange={(e) => setSelectedVisibilityFilter(e.target.value as any)}
              aria-label="Filtrar por Visibilidade"
              className="w-full sm:w-auto bg-slate-950 border border-slate-800 text-slate-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-purple-500 appearance-none pr-8 cursor-pointer font-bold truncate"
            >
              <option value="all">Visibilidades</option>
              <option value="public">Apenas Públicos</option>
              <option value="private">Apenas Privados</option>
            </select>
            <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
          </div>

          {/* Sort By */}
          <div className="relative w-full sm:w-auto">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              aria-label="Ordenar por"
              className="w-full sm:w-auto bg-slate-950 border border-slate-800 text-slate-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-purple-500 appearance-none pr-8 cursor-pointer font-bold truncate"
            >
              <option value="recent">Mais Recentes</option>
              <option value="name">Nome (A-Z)</option>
              <option value="cards">Mais Cartas</option>
              <option value="value">Maior Valor</option>
            </select>
            <i className="fas fa-sort absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
          </div>

          {/* View Mode (Col-span on mobile to sit cleanly alongside) */}
          <div className="col-span-2 sm:col-span-1 flex items-center justify-end sm:justify-start bg-slate-950 border border-slate-800 rounded-xl p-0.5 shrink-0 ml-auto sm:ml-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-1.5 ${
                viewMode === 'grid' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
              }`}
              title="Visualização em Grade"
            >
              <i className="fas fa-th-large text-[11px]"></i>
              <span className="sm:hidden text-[10px] font-bold">Grade</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-1.5 ${
                viewMode === 'list' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
              }`}
              title="Visualização em Lista"
            >
              <i className="fas fa-list text-[11px]"></i>
              <span className="sm:hidden text-[10px] font-bold">Lista</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Decks Content */}
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center space-y-4 text-center">
          <div className="w-10 h-10 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
          <p className="text-xs font-bold text-slate-400">Carregando seus decks...</p>
        </div>
      ) : filteredDecks.length === 0 ? (
        <div className="py-20 bg-slate-900/40 border border-slate-800 rounded-3xl flex flex-col items-center justify-center space-y-4 text-center p-6">
          <div className="w-16 h-16 rounded-2xl bg-purple-950/40 border border-purple-500/20 flex items-center justify-center text-purple-400 text-2xl">
            <i className="fas fa-box-open"></i>
          </div>
          <div className="space-y-1 max-w-md">
            <h3 className="text-lg font-black text-white uppercase italic">
              {decks.length === 0 ? 'Nenhum deck criado ainda' : 'Nenhum deck encontrado com os filtros atuais'}
            </h3>
            <p className="text-xs text-slate-400">
              {decks.length === 0
                ? 'Comece agora criando seu primeiro baralho. Você definirá o nome e o jogo aqui, e depois adicionará as cartas no Deckbuilder.'
                : 'Tente alterar os termos de busca ou filtros para localizar seus baralhos.'}
            </p>
          </div>
          <div className="flex items-center space-x-3 pt-2">
            {decks.length > 0 && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedGameFilter('all');
                  setSelectedFormatFilter('all');
                  setSelectedVisibilityFilter('all');
                }}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase tracking-wider rounded-xl transition-all cursor-pointer"
              >
                Limpar Filtros
              </button>
            )}
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/30 transition-all cursor-pointer"
            >
              Criar Novo Deck
            </button>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        /* GRID VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredDecks.map((deck) => {
            const meta = processDeckMeta(deck);
            const isCloning = cloningDeckId === deck.id;

            return (
              <motion.div
                key={deck.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
                className={`bg-slate-900 border border-slate-800 hover:border-purple-500/40 rounded-2xl flex flex-col justify-between transition-all duration-200 shadow-md hover:shadow-purple-600/10 group relative ${
                  openMenuDeckId === deck.id ? 'z-30 border-purple-500/50' : ''
                }`}
              >
                {/* Artwork Hero Header */}
                <div className="relative h-36 sm:h-40 w-full bg-slate-950 overflow-hidden rounded-t-2xl">
                  {meta.leaderImage ? (
                    <>
                      {/* Lightweight background layer without expensive Gaussian blur filter */}
                      <div
                        className="absolute inset-0 bg-cover bg-center opacity-15 pointer-events-none"
                        style={{ backgroundImage: `url(${meta.leaderImage})` }}
                      ></div>
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-950/80 pointer-events-none"></div>

                      <div className="absolute right-3 sm:right-4 top-2.5 w-18 sm:w-20 h-28 sm:h-32 rounded-lg overflow-hidden border border-white/20 shadow-md">
                        <CardImage
                          src={meta.leaderImage}
                          alt={deck.name}
                          objectFit="cover"
                          className="w-full h-full"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-950/40 to-slate-950">
                      <i className={`fas ${getGameIcon(deck.game_id)} text-4xl sm:text-5xl text-white/10`}></i>
                    </div>
                  )}

                  {/* Metadata overlay on art (safe width calculation preventing collision with card) */}
                  <div className="relative z-10 p-3.5 sm:p-4 flex flex-col justify-between h-full max-w-[calc(100%-5.5rem)] sm:max-w-[calc(100%-6rem)]">
                    <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                      <span className="px-2 py-0.5 bg-purple-950/80 text-purple-300 text-[9px] font-bold uppercase rounded border border-purple-500/30">
                        {meta.gameName}
                      </span>
                      {meta.format && (
                        <span className="px-2 py-0.5 bg-slate-900/80 text-slate-300 text-[9px] font-bold uppercase rounded border border-slate-700">
                          {meta.format}
                        </span>
                      )}
                    </div>

                    <div className="space-y-1">
                      <Link
                        to={`/deckbuilder?deckId=${deck.id}`}
                        className="text-sm sm:text-base font-black text-white hover:text-purple-300 transition-colors uppercase tracking-tight line-clamp-2 italic drop-shadow-sm"
                        title="Abrir no editor Deckbuilder"
                      >
                        {deck.name}
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Details Section */}
                <div className="p-3.5 sm:p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/80 text-center">
                    <div>
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block">
                        Total de Cartas
                      </span>
                      <span className="text-xs font-black text-slate-200 font-mono tabular-nums">
                        {meta.totalCards} cartas
                      </span>
                    </div>
                    <div>
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block">
                        Valor Estimado
                      </span>
                      <span className={`text-xs font-black font-mono tabular-nums ${meta.estimatedValue > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {meta.estimatedValue > 0 ? `R$ ${meta.estimatedValue.toFixed(2)}` : 'S/ Preço'}
                      </span>
                    </div>
                  </div>

                  {/* Status, Date & Visibility Switch */}
                  <div className="flex items-center justify-between text-[11px] pt-1">
                    <button
                      onClick={(e) => handleTogglePrivacy(deck, e)}
                      className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider flex items-center space-x-1 border transition-colors cursor-pointer ${
                        deck.is_public
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                          : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700 hover:text-slate-300'
                      }`}
                      title="Clique para alternar visibilidade (Público/Privado)"
                    >
                      <i className={`fas ${deck.is_public ? 'fa-globe' : 'fa-lock'} text-[8px]`}></i>
                      <span>{deck.is_public ? 'Público' : 'Privado'}</span>
                    </button>

                    <span className="text-[10px] text-slate-500 font-mono tabular-nums">
                      {new Date(deck.updated_at || deck.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>

                {/* Action Buttons Footer */}
                <div className="p-3 bg-slate-950/70 border-t border-slate-800 flex items-center justify-between gap-2 rounded-b-2xl">
                  {/* Editar Cartas (Ação Principal) */}
                  <button
                    type="button"
                    onClick={() => {
                      if (setActiveGame && deck.game_id) {
                        setActiveGame(Number(deck.game_id));
                      }
                      navigate(`/deckbuilder?deckId=${deck.id}`);
                    }}
                    className="flex-1 p-2 bg-purple-600 hover:bg-purple-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center space-x-2 shadow-md shadow-purple-600/20 active:scale-95 cursor-pointer"
                    title="Editar cartas no Deckbuilder"
                  >
                    <span>Editar</span>
                  </button>

                  {/* Botão Opções com Dropdown */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuDeckId(openMenuDeckId === deck.id ? null : deck.id);
                      }}
                      className={`px-3 py-2 rounded-xl border text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer active:scale-95 ${
                        openMenuDeckId === deck.id
                          ? 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-600/30'
                          : 'bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border-slate-700'
                      }`}
                      title="Mais opções do baralho"
                    >
                      <i className="fas fa-ellipsis-v text-xs"></i>
                      <span className="text-[11px] font-bold">Opções</span>
                    </button>

                    <AnimatePresence>
                      {openMenuDeckId === deck.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: 6 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: 6 }}
                          transition={{ duration: 0.12 }}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute right-0 bottom-full mb-2 w-56 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl p-1.5 z-50 space-y-0.5"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setOpenMenuDeckId(null);
                              openEditInfoModal(deck);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-sliders-h text-purple-400 w-4 text-center"></i>
                            <span>Editar Informações</span>
                          </button>

                          <Link
                            to={`/deck/${deck.id}`}
                            onClick={() => setOpenMenuDeckId(null)}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-eye text-blue-400 w-4 text-center"></i>
                            <span>Visualizar Deck</span>
                          </Link>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleCloneDeck(deck, e);
                            }}
                            disabled={isCloning}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer disabled:opacity-50"
                          >
                            <i className={`fas ${isCloning ? 'fa-spinner fa-spin' : 'fa-copy'} text-indigo-400 w-4 text-center`}></i>
                            <span>Duplicar Baralho</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleExportText(deck, e);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-file-export text-emerald-400 w-4 text-center"></i>
                            <span>Copiar Lista (Texto)</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleShareLink(deck, e);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-share-alt text-amber-400 w-4 text-center"></i>
                            <span>Compartilhar Link</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleTogglePrivacy(deck, e);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className={`fas ${deck.is_public ? 'fa-lock' : 'fa-globe'} text-cyan-400 w-4 text-center`}></i>
                            <span>{deck.is_public ? 'Tornar Privado' : 'Tornar Público'}</span>
                          </button>

                          <div className="my-1 border-t border-slate-800/80"></div>

                          <button
                            type="button"
                            onClick={() => {
                              setOpenMenuDeckId(null);
                              setDeckToDelete(deck);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-trash-alt text-red-400 w-4 text-center"></i>
                            <span>Excluir Baralho</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        /* LIST VIEW */
        <div className="space-y-2.5">
          {filteredDecks.map((deck) => {
            const meta = processDeckMeta(deck);
            const isCloning = cloningDeckId === deck.id;

            return (
              <motion.div
                key={deck.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-slate-900 border border-slate-800 hover:border-purple-500/30 p-3 sm:p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-all relative ${
                  openMenuDeckId === deck.id ? 'z-30 border-purple-500/40' : ''
                }`}
              >
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-12 h-16 rounded-lg overflow-hidden bg-slate-950 border border-slate-800 shrink-0 flex items-center justify-center shadow">
                    {meta.leaderImage ? (
                      <CardImage
                        src={meta.leaderImage}
                        alt={deck.name}
                        objectFit="cover"
                        className="w-full h-full"
                      />
                    ) : (
                      <i className={`fas ${getGameIcon(deck.game_id)} text-purple-400 text-lg`}></i>
                    )}
                  </div>

                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-y-0.5">
                      <span className="px-1.5 py-0.5 bg-purple-950/80 text-purple-300 text-[8.5px] font-bold uppercase rounded border border-purple-500/30">
                        {meta.gameName}
                      </span>
                      {meta.format && (
                        <span className="px-1.5 py-0.5 bg-slate-800 text-slate-300 text-[8.5px] font-bold uppercase rounded border border-slate-700">
                          {meta.format}
                        </span>
                      )}
                      <span className="text-[10px] text-slate-500 font-mono tabular-nums font-bold">
                        {meta.totalCards} cartas
                      </span>
                    </div>

                    <Link
                      to={`/deckbuilder?deckId=${deck.id}`}
                      className="text-sm font-black text-white hover:text-purple-300 transition-colors uppercase tracking-tight block truncate italic"
                    >
                      {deck.name}
                    </Link>

                    <div className="flex items-center space-x-2 text-[10px] text-slate-500 font-mono tabular-nums">
                      <span>Atualizado em {new Date(deck.updated_at || deck.created_at).toLocaleDateString('pt-BR')}</span>
                      <span>·</span>
                      <button
                        onClick={(e) => handleTogglePrivacy(deck, e)}
                        className={`text-[9px] font-bold uppercase tracking-wider ${
                          deck.is_public ? 'text-emerald-400 hover:underline' : 'text-slate-400 hover:underline'
                        }`}
                      >
                        {deck.is_public ? 'Público' : 'Privado'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto shrink-0 pt-2.5 sm:pt-0 border-t sm:border-t-0 border-slate-800">
                  {meta.estimatedValue > 0 && (
                    <span className="text-xs font-black text-emerald-400 font-mono tabular-nums">
                      R$ {meta.estimatedValue.toFixed(2)}
                    </span>
                  )}

                  <div className="flex items-center gap-2 ml-auto sm:ml-0">
                    <button
                      onClick={() => {
                        if (setActiveGame && deck.game_id) {
                          setActiveGame(Number(deck.game_id));
                        }
                        navigate(`/deckbuilder?deckId=${deck.id}`);
                      }}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white font-black text-[10px] uppercase tracking-wider rounded-lg transition-all flex items-center space-x-1.5 cursor-pointer shadow-sm active:scale-95"
                      title="Editar cartas no Deckbuilder"
                    >
                      <span>Editar</span>
                    </button>

                    {/* Botão Opções com Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuDeckId(openMenuDeckId === deck.id ? null : deck.id);
                        }}
                        className={`p-1.5 px-2.5 rounded-lg border text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95 ${
                          openMenuDeckId === deck.id
                            ? 'bg-purple-600 text-white border-purple-500 shadow-sm'
                            : 'bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700'
                        }`}
                        title="Mais opções do baralho"
                      >
                        <i className="fas fa-ellipsis-v text-[10px]"></i>
                        <span className="text-[10px] font-bold">Opções</span>
                      </button>

                    <AnimatePresence>
                      {openMenuDeckId === deck.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: 6 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: 6 }}
                          transition={{ duration: 0.12 }}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute right-0 bottom-full mb-2 w-56 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl p-1.5 z-50 space-y-0.5"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setOpenMenuDeckId(null);
                              openEditInfoModal(deck);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-sliders-h text-purple-400 w-4 text-center"></i>
                            <span>Editar Informações</span>
                          </button>

                          <Link
                            to={`/deck/${deck.id}`}
                            onClick={() => setOpenMenuDeckId(null)}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-eye text-blue-400 w-4 text-center"></i>
                            <span>Visualizar Deck</span>
                          </Link>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleCloneDeck(deck, e);
                            }}
                            disabled={isCloning}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer disabled:opacity-50"
                          >
                            <i className={`fas ${isCloning ? 'fa-spinner fa-spin' : 'fa-copy'} text-indigo-400 w-4 text-center`}></i>
                            <span>Duplicar Baralho</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleExportText(deck, e);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-file-export text-emerald-400 w-4 text-center"></i>
                            <span>Copiar Lista (Texto)</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleShareLink(deck, e);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-share-alt text-amber-400 w-4 text-center"></i>
                            <span>Compartilhar Link</span>
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              setOpenMenuDeckId(null);
                              handleTogglePrivacy(deck, e);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className={`fas ${deck.is_public ? 'fa-lock' : 'fa-globe'} text-cyan-400 w-4 text-center`}></i>
                            <span>{deck.is_public ? 'Tornar Privado' : 'Tornar Público'}</span>
                          </button>

                          <div className="my-1 border-t border-slate-800/80"></div>

                          <button
                            type="button"
                            onClick={() => {
                              setOpenMenuDeckId(null);
                              setDeckToDelete(deck);
                            }}
                            className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors flex items-center gap-2.5 cursor-pointer"
                          >
                            <i className="fas fa-trash-alt text-red-400 w-4 text-center"></i>
                            <span>Excluir Baralho</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </motion.div>
            );
          })}
        </div>
      )}

      {/* MODAL NOVO DECK */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 animate-fade-in">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl relative space-y-5"
            >
              <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-600/20 text-purple-400 flex items-center justify-center">
                    <i className="fas fa-plus text-xs"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white uppercase italic">
                      Criar Novo Baralho
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Configure as informações básicas e abra diretamente no Deckbuilder
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleCreateDeckSubmit} className="space-y-4">
                {/* Nome do Deck */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Nome do Baralho *
                  </label>
                  <input
                    type="text"
                    required
                    value={newDeckName}
                    onChange={(e) => setNewDeckName(e.target.value)}
                    placeholder="Ex: Mono Blue Tempo, Charizard EX, Luffy Aggro..."
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>

                {/* Seleção de Jogo */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Jogo (TCG) *
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1 bg-slate-950/60 rounded-xl border border-slate-800">
                    {gamesList.map((g) => {
                      const isSelected = String(newDeckGameId) === String(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => setNewDeckGameId(g.id)}
                          className={`p-2 rounded-lg text-left text-xs font-bold flex items-center space-x-2 transition-all cursor-pointer ${
                            isSelected
                              ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30 border border-purple-400'
                              : 'bg-slate-900/90 text-slate-300 hover:text-white hover:bg-slate-800 border border-slate-800'
                          }`}
                        >
                          <i className={`fas ${getGameIcon(g.id)} text-[11px] shrink-0`}></i>
                          <span className="truncate">{g.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Seleção de Formato */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Formato de Jogo
                  </label>
                  {loadingFormats ? (
                    <div className="text-xs text-slate-500 py-2">Carregando formatos...</div>
                  ) : availableFormats.length > 0 ? (
                    <div className="relative">
                      <select
                        value={newDeckFormatId}
                        onChange={(e) => setNewDeckFormatId(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 text-slate-200 text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-purple-500 appearance-none pr-8 cursor-pointer font-bold"
                      >
                        {availableFormats.map((f) => (
                          <option key={f.id} value={f.id} title={f.description || undefined}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 italic py-1">
                      Formato padrão (Classic) será aplicado automaticamente.
                    </div>
                  )}
                </div>

                {/* Visibilidade Público / Privado */}
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-slate-200 block">
                      Tornar Baralho Público
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {newDeckIsPublic
                        ? 'Visível para a comunidade na página de Decks'
                        : 'Privado (apenas você poderá visualizar)'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewDeckIsPublic(!newDeckIsPublic)}
                    className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                      newDeckIsPublic ? 'bg-purple-600' : 'bg-slate-800'
                    }`}
                  >
                    <span
                      className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                        newDeckIsPublic ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Ações */}
                <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingDeck || !newDeckName.trim() || !newDeckGameId}
                    className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/30 flex items-center space-x-2 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {isCreatingDeck ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Criando...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-hammer"></i>
                        <span>Criar e Abrir no Deckbuilder</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      <AnimatePresence>
        {deckToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 animate-fade-in">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl relative space-y-4"
            >
              <div className="flex items-center space-x-3 text-rose-500">
                <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-lg">
                  <i className="fas fa-trash-alt"></i>
                </div>
                <div>
                  <h3 className="text-base font-black text-white uppercase italic">
                    Excluir Baralho
                  </h3>
                  <p className="text-xs text-slate-400">Esta ação é permanente e irreversível.</p>
                </div>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Tem certeza que deseja excluir o baralho <strong>&quot;{deckToDelete.name}&quot;</strong>? Todas as cartas configuradas neste deck serão removidas.
              </p>

              <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setDeckToDelete(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-black uppercase tracking-wider rounded-xl transition-colors flex items-center space-x-1.5 cursor-pointer shadow-lg shadow-rose-600/30"
                >
                  {isDeleting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Excluindo...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-alt"></i>
                      <span>Confirmar Exclusão</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL EDITAR INFORMAÇÕES DO DECK */}
      <AnimatePresence>
        {deckToEdit && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 animate-fade-in">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl relative space-y-5"
            >
              <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 rounded-lg bg-purple-600/20 text-purple-400 flex items-center justify-center">
                    <i className="fas fa-pen text-xs"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-black text-white uppercase italic">
                      Editar Informações do Baralho
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Altere o nome, formato ou visibilidade do seu baralho
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setDeckToEdit(null)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSaveEditSubmit} className="space-y-4">
                {/* Nome do Deck */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Nome do Baralho *
                  </label>
                  <input
                    type="text"
                    required
                    value={editDeckName}
                    onChange={(e) => setEditDeckName(e.target.value)}
                    placeholder="Nome do baralho..."
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
                  />
                </div>

                {/* Formato do Jogo */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
                    Formato de Jogo
                  </label>
                  {loadingEditFormats ? (
                    <div className="text-xs text-slate-500 py-2">Carregando formatos...</div>
                  ) : editFormats.length > 0 ? (
                    <div className="relative">
                      <select
                        value={editDeckFormatId}
                        onChange={(e) => setEditDeckFormatId(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 text-slate-200 text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-purple-500 appearance-none pr-8 cursor-pointer font-bold"
                      >
                        <option value="">Formato Padrão</option>
                        {editFormats.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 italic py-1">
                      Nenhum formato alternativo configurado para este jogo.
                    </div>
                  )}
                </div>

                {/* Visibilidade Público / Privado */}
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="text-xs font-bold text-slate-200 block">
                      Tornar Baralho Público
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {editDeckIsPublic
                        ? 'Visível para a comunidade na página de Decks'
                        : 'Privado (apenas você poderá visualizar)'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditDeckIsPublic(!editDeckIsPublic)}
                    className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${
                      editDeckIsPublic ? 'bg-purple-600' : 'bg-slate-800'
                    }`}
                  >
                    <span
                      className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                        editDeckIsPublic ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Ações */}
                <div className="flex items-center justify-end space-x-3 pt-3 border-t border-slate-800">
                  <button
                    type="button"
                    onClick={() => setDeckToEdit(null)}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingEdit || !editDeckName.trim()}
                    className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/30 flex items-center space-x-2 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {isSavingEdit ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        <span>Salvando...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        <span>Salvar Informações</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
